import {
  AdgroupFormData,
  AppEvents,
  BidStrategyType,
  CampaignFormData,
  StrategyListItem,
  StatisticType,
  OptimizeGoal,
  VideoDurationType,
} from '@tt4b/ttam-enums';
import {
  useAdgroupAppEvents,
  useAdgroupBidTypes,
  useStdAdgroupIs,
  useStdObjectiveType,
  useStdCreativeAdgroupIs,
} from '@tt4b/ttam-hooks';
import { Ref, computed } from '@vue/composition-api';
import { i18n } from './../../../../i18n';
import { useCurrencyOption } from './../../../../hooks/creation/account/useCurrencyOption/index';

export function useCommonPropsLearnMore(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = props;
  const { isWebsite } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isProductFromShop, isProductFromShowcase, isLiveAds } = useStdCreativeAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const learnMore = (type: BidStrategyType): string | undefined => {
    if (type === BidStrategyType.HIGHEST_VALUE || type === BidStrategyType.MIN_ROAS) {
      // VSA下选shop/showcase、LSA下，都切换到新链接（TikTok shop的帮助文档）
      return isProductFromShop.value || isProductFromShowcase.value || isLiveAds.value
        ? '/help/article?aid=10014231'
        : isWebsite.value
        ? '/help/article?aid=10008856'
        : '/help/article?aid=10007025';
    }

    return;
  };

  return {
    learnMore,
  };
}

export function useCommonPropsCurrencyOption() {
  return {
    currencyOption: useCurrencyOption()?.currencyOption,
  };
}

export function useCommonPropsCurrencyUnit(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appEvents: Ref<AppEvents>;
  strategyList: Ref<StrategyListItem[]>;
}) {
  const { adgroupData, strategyList, appEvents, campaignData } = props;
  const { curExternalActionItem } = useAdgroupAppEvents({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
    appEvents,
  });

  const { isAppInstall } = useStdObjectiveType({
    campaignFormData: campaignData,
  });

  /** 出价单位 */
  const bidUnit = computed(() => {
    switch (adgroupData.value?.optimizeGoal) {
      case OptimizeGoal.CONVERT: {
        return i18n.t('adgroup_bid_conversion');
      }
      case OptimizeGoal.CLICK: {
        return i18n.t('adgroup_bid_click');
      }
      case OptimizeGoal.SHOW:
      case OptimizeGoal.REACH: {
        return i18n.t('adgroup_bid_impression_hint');
      }
      case OptimizeGoal.LANDING_PAGE_VIEW: {
        return i18n.t('adgroup_optimize_landing_page_view');
      }
      case OptimizeGoal.VIDEO_VIEW: {
        return adgroupData.value?.cpvVideoDurationType === VideoDurationType.TWO_SECONDS
          ? i18n.t('module_adgroup_bid_videoview_hint_2s')
          : i18n.t('module_adgroup_bid_videoview_hint_6s');
      }
      case OptimizeGoal.INSTALL: {
        return i18n.t('adgroup_bid_appinstall');
      }
      case OptimizeGoal.IN_APP_EVENT: {
        const type = i18n.t(curExternalActionItem.value?.external_action_i18nkey || '');

        if (!isAppInstall.value) {
          return type;
        }

        return adgroupData.value?.statisticType === StatisticType.EACH_EVENT
          ? i18n.t('module_bid_unit_each', { type })
          : i18n.t('module_bid_unit_unique', { type });
      }
      case OptimizeGoal.LEAD: {
        return i18n.t('module_lead_ads_optimizal_hint');
      }
      case OptimizeGoal.FANS: {
        return i18n.t('adgroup_bid_fans');
      }
      case OptimizeGoal.WEBVIEWS: {
        return i18n.t('adgroup_bid_web_views');
      }
      case OptimizeGoal.VALUE: {
        // 目前只有一种情况，所以暂时写死
        return i18n.t('module_value_optimization_hint');
      }
      case OptimizeGoal.PRODUCT_CLICK_IN_LIVE:
        return i18n.t('module_adgroup_optimize_conversion_product_link');
      case OptimizeGoal.MT_LIVE_ROOM:
        return i18n.t('adgroup_bid_conversion');
      case OptimizeGoal.DESTINATION_VISIT:
        return i18n.t('adgroup_optimize_destination_visit');
      default: {
        return '';
      }
    }
  });

  const { currencyOption } = useCurrencyOption();

  const { isHighestValue } = useAdgroupBidTypes({
    adgroupFormData: adgroupData,
    strategyList,
  });

  return {
    currencyUnit: computed(() => (isHighestValue.value ? '' : `${currencyOption.value.currency}/${bidUnit.value}`)),
  };
}
